import React, { useEffect, useState } from "react";
import { Recycle, Users, Star } from "lucide-react";
import { axios, API, logError } from "../lib/api";

/**
 * Live impact counters strip — sits directly below the homepage hero.
 *
 * Animates from 0 → fetched value over ~1s so the page feels alive on load.
 * Falls back to a friendly "growing every day" copy if the API errors.
 */
const ITEMS = [
  { id: "toys_saved",      label: "Toys saved from landfill",        icon: <Recycle className="w-5 h-5" />, color: "text-emerald-600" },
  { id: "families_served", label: "Families given a second-hand toy", icon: <Users   className="w-5 h-5" />, color: "text-[#4A90E2]"  },
  { id: "reviews",         label: "Five-star community reviews",     icon: <Star    className="w-5 h-5" />, color: "text-[#FFD93D]"  },
];

const useCountUp = (target, durationMs = 1000) => {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!Number.isFinite(target) || target <= 0) { setValue(0); return; }
    const start = performance.now();
    let raf;
    const tick = (t) => {
      const progress = Math.min((t - start) / durationMs, 1);
      // Ease-out cubic for a softer landing.
      const eased = 1 - (1 - progress) ** 3;
      setValue(Math.round(eased * target));
      if (progress < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs]);
  return value;
};

const formatNumber = (n) => new Intl.NumberFormat("en-GB").format(n || 0);

export const StatsStrip = () => {
  const [stats, setStats] = useState(null);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/marketplace/stats`);
        if (!cancelled) setStats(r.data);
      } catch (err) {
        if (!cancelled) setErrored(true);
        logError("marketplace-stats", err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (errored) {
    // Avoid showing zeroed counters if the API fails — fall back gracefully.
    return (
      <div
        className="bg-white border-2 border-gray-900 rounded-2xl p-5 mb-8 shadow-[3px_3px_0_0_#111827]"
        data-testid="stats-strip-fallback"
      >
        <p className="text-sm font-bold text-center text-gray-700">
          Thousands of families are buying, selling, and swapping toys on ToyCycleHub every week.
        </p>
      </div>
    );
  }

  return (
    <div
      className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-8"
      data-testid="stats-strip"
    >
      {ITEMS.map((item) => (
        <StatCard
          key={item.id}
          id={item.id}
          icon={item.icon}
          label={item.label}
          color={item.color}
          value={stats ? stats[item.id] : 0}
          loading={!stats}
        />
      ))}
    </div>
  );
};

const StatCard = ({ icon, label, color, value, loading }) => {
  const animated = useCountUp(value || 0);
  return (
    <div
      className="bg-white border-2 border-gray-900 rounded-2xl p-5 shadow-[3px_3px_0_0_#111827] flex items-center gap-4"
      data-testid={`stat-card-${label.replace(/\s+/g, "-").toLowerCase()}`}
    >
      <span className={`w-12 h-12 rounded-full bg-[#FFF6C7] border-2 border-gray-900 flex items-center justify-center ${color}`}>
        {icon}
      </span>
      <div className="leading-tight">
        <div
          className="text-2xl sm:text-3xl font-black tabular-nums"
          style={{ fontFamily: "Outfit" }}
          data-testid="stat-card-value"
        >
          {loading ? "—" : formatNumber(animated)}
        </div>
        <div className="text-xs sm:text-sm font-bold text-gray-700">{label}</div>
      </div>
    </div>
  );
};
